import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Words that must not be sentence-cased. The label is rendered with CSS
// `text-transform: capitalize`, which turns a route segment like "ai-settings" into
// "Ai Settings". Upper-casing these here survives that, because capitalize only touches the
// first letter of each word and leaves the rest alone.
const ACRONYMS = {
  ai: "AI",
  api: "API",
  s3: "S3",
  sql: "SQL",
  lcp: "LCP",
  afms: "AFMS",
  loto: "LOTO",
  qr: "QR",
};

function formatLabel(segment) {
  return (segment || "")
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => ACRONYMS[word.toLowerCase()] || word)
    .join(" ");
}

function Breadcrumbs({ icon = "home", title, route, light = false }) {
  const segments = Array.isArray(route) ? route : String(route || "").split("/").filter(Boolean);
  const trail = segments.slice(0, -1);
  const color = light ? "white" : "dark";

  return (
    <SoftBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": { color: ({ palette }) => (light ? palette.white.main : palette.grey[600]) },
        }}
      >
        <Link to="/">
          <SoftTypography component="span" variant="body2" color={color} opacity={light ? 0.8 : 0.5} sx={{ lineHeight: 0 }}>
            <Icon>{icon}</Icon>
          </SoftTypography>
        </Link>
        {trail.map((segment, idx) => {
          const href = "/" + segments.slice(0, idx + 1).join("/");
          return (
            <Link key={href} to={href}>
              <SoftTypography
                component="span"
                variant="button"
                fontWeight="regular"
                textTransform="capitalize"
                color={color}
                opacity={light ? 0.8 : 0.5}
              >
                {formatLabel(segment)}
              </SoftTypography>
            </Link>
          );
        })}
        <SoftTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={color}
        >
          {formatLabel(segments[segments.length - 1] ?? title)}
        </SoftTypography>
      </MuiBreadcrumbs>
      <SoftTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={color}
        noWrap
      >
        {formatLabel(title)}
      </SoftTypography>
    </SoftBox>
  );
}

Breadcrumbs.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
